/**
 * Portfolio section with lightbox
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbox } from '../ui/Lightbox';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import portfolioData from '@/data/portfolio.json';

// Import all portfolio JSON files
// These will be auto-discovered at build time
const portfolioModules = import.meta.glob('/src/data/portfolio/*.json', { eager: true });

// Convert modules to array
const portfolioItems = Object.values(portfolioModules).map((mod: any) => {
  const item = mod.default;
  return {
    id: item.id,
    src: item.image,
    thumbnail: item.image,
    title: ''
  };
});

const sectionTitle = portfolioData.section_title;
const sectionTitleParts = portfolioData.section_title_parts || [{ text: sectionTitle, highlight: false }];
const sectionSubtitle = portfolioData.section_subtitle;

export function Portfolio() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [items, setItems] = useState(portfolioItems);

  const { ref, isIntersecting } = useIntersectionObserver({ threshold: 0.1 });

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  // Reload portfolio items when component mounts (for hot reload)
  useState(() => {
    const reloadItems = async () => {
      const modules = import.meta.glob('/src/data/portfolio/*.json', { eager: true });
      const newItems = Object.values(modules).map((mod: any) => {
        const item = mod.default;
        return {
          id: item.id,
          src: item.image,
          thumbnail: item.image,
          title: ''
        };
      });
      setItems(newItems);
    };
    reloadItems();
  });

  return (
    <section id="portfolio" ref={ref} className="section" style={{ backgroundColor: 'var(--color-gray-50)' }}>
      <div className="container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isIntersecting ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="has-text-centered mb-6"
        >
          <h2 className="title is-2 has-text-weight-bold">
            {sectionTitleParts.map((part, index) => (
              part.highlight ? (
                <span key={index} className="gradient-text">{part.text}</span>
              ) : (
                <span key={index}>{part.text}</span>
              )
            ))}
          </h2>
          <p className="subtitle is-5" style={{ maxWidth: '600px', margin: '0 auto' }}>
            {sectionSubtitle}
          </p>
        </motion.div>

        {/* Gallery Grid */}
        {items.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="has-text-centered py-6"
            style={{ padding: '4rem 0', color: 'var(--color-gray-400)' }}
          >
            <p>Sem projetos para mostrar ainda.</p>
          </motion.div>
        ) : (
          <div className="portfolio-grid">
            <AnimatePresence>
              {items.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3, delay: Math.min(index * 0.05, 0.5) }}
                  layout
                  className="portfolio-item"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={item.thumbnail || item.src}
                    alt=""
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

      </div>

      {/* Lightbox */}
      <Lightbox
        items={items}
        initialIndex={currentImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </section>
  );
}